/**
 * NoticeSheet -- the notification Joseph tapped, in full: title, the whole
 * text, when it arrived, and one button to the thing it is about.
 *
 * Mounted in the shell so it shows over whichever screen is up. It opens two
 * ways, both from the service worker (public/sw.js, notificationclick):
 *   - the app was closed: the worker opens `/today?notice=<id>`;
 *   - the app was open: the worker posts the id to this window, which answers
 *     so the worker knows not to reload it.
 * Only the id travels; the words are read from what the device saved
 * (core/push/notices.ts). Look and behaviour follow the Today item sheet
 * (modules/today/JarvisFeed.tsx), whose styles it reuses.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { loadNotice, safeNoticeLink, type SavedNotice } from '../core/push/notices';
import { formatMessageDateDivider, formatTime } from '../core/utils/time';
import css from '../modules/today/today.module.css';

/** `?notice=<id>`: its own query parameter, apart from the Today hash jump and `?item=`. */
const MARKER = 'notice';
/** history.state flag on the entry the sheet added, so Close can step back off it. */
const SHEET_ENTRY = 'noticeSheet';
const DOCUMENT_ID = `${Date.now()}-${Math.random()}`;
const MISSING = 'That notification is no longer saved on this phone.';
const CLOSE_FALLBACK_MS = 700;

const SCREENS: Record<string, string> = {
  '': 'Chat',
  chat: 'Chat',
  today: 'Today',
  approvals: 'Approvals',
  deals: 'Deals',
  people: 'People',
  finance: 'Finance',
  trading: 'Trading',
};

function markerInAddress(discardEarlier = false): string | null {
  if (discardEarlier && window.history.state?.[SHEET_ENTRY]) {
    window.history.replaceState(null, '', addressWith(null));
  }
  return new URLSearchParams(window.location.search).get(MARKER);
}

function addressWith(id: string | null): string {
  const url = new URL(window.location.href);
  if (id) url.searchParams.set(MARKER, id);
  else url.searchParams.delete(MARKER);
  return `${url.pathname}${url.search}${url.hash}`;
}

function linkLabel(link: string): string {
  const screen = SCREENS[link.split(/[/?#]/)[1] ?? ''];
  return screen ? `Open in ${screen}` : 'Open';
}

/** "Arrived today at 6:33 PM", "Arrived yesterday at ...", "Arrived Sat, Oct 3 at ...". */
function arrivalWords(at: number): string {
  const when = new Date(at);
  const day = formatMessageDateDivider(when);
  const time = formatTime(when);
  if (!day || !time) return '';
  return `Arrived ${day === 'Today' || day === 'Yesterday' ? day.toLowerCase() : day} at ${time}`;
}

export function NoticeSheet({ missingOnLaunch = false }: { missingOnLaunch?: boolean }) {
  const [noticeId, setNoticeId] = useState<string | null>(() => {
    const traversed = performance.getEntriesByType?.('navigation').some((entry) => (entry as PerformanceNavigationTiming).type === 'back_forward');
    const id = markerInAddress(traversed);
    return missingOnLaunch ? '' : id;
  });
  // undefined while the device is being read, null when the notice is not there.
  const [notice, setNotice] = useState<SavedNotice | null | undefined>(undefined);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const closing = useRef(false);

  useEffect(() => {
    // Launched on a notice: put the plain screen underneath, so Back closes the
    // sheet and stays in the app instead of leaving it.
    const launchedOn = markerInAddress();
    if (launchedOn && !window.history.state?.[SHEET_ENTRY]) {
      window.history.replaceState(window.history.state, '', addressWith(null));
      window.history.pushState({ [SHEET_ENTRY]: DOCUMENT_ID }, '', addressWith(launchedOn));
    }

    function syncFromAddress(event: PopStateEvent | PageTransitionEvent) {
      if ('persisted' in event && !event.persisted) return;
      closing.current = false;
      const earlier = window.history.state?.[SHEET_ENTRY] !== DOCUMENT_ID;
      setNoticeId(markerInAddress(earlier || ('persisted' in event && event.persisted)));
    }

    function onWorkerMessage(event: MessageEvent) {
      const id: unknown = event.data?.type === 'jarvis-notice' ? event.data.id : null;
      // Too late to answer (this page was frozen): the worker is already reloading
      // this window on the notice, so opening it here too would leave a stray entry.
      if (typeof id !== 'string' || Date.now() > event.data.answerBy) return;
      // Answer first: a worker that hears nothing reloads this window on the notice.
      event.ports[0]?.postMessage('shown');
      // A second tap while a sheet is open replaces it, so one Back still closes it.
      if (markerInAddress()) window.history.replaceState(window.history.state, '', addressWith(id));
      else window.history.pushState({ [SHEET_ENTRY]: DOCUMENT_ID }, '', addressWith(id));
      setNoticeId(id);
    }

    window.addEventListener('popstate', syncFromAddress);
    window.addEventListener('pageshow', syncFromAddress);
    navigator.serviceWorker?.addEventListener('message', onWorkerMessage);
    return () => {
      window.removeEventListener('popstate', syncFromAddress);
      window.removeEventListener('pageshow', syncFromAddress);
      navigator.serviceWorker?.removeEventListener('message', onWorkerMessage);
    };
  }, []);

  useEffect(() => {
    setNotice(undefined);
    if (noticeId === null) return;
    let current = true;
    void loadNotice(noticeId).then((found) => {
      if (current) setNotice(found);
    });
    return () => { current = false; };
  }, [noticeId]);

  function clearMarkerInPlace() {
    closing.current = false;
    window.history.replaceState(null, '', addressWith(null));
    setNoticeId(null);
  }

  function close() {
    if (closing.current) return;
    if (!window.history.state?.[SHEET_ENTRY]) {
      clearMarkerInPlace();
      return;
    }
    // The sheet added this entry: step back off it, as the phone's Back does.
    // A second tap on Close must not step back twice, and a step that never
    // lands must not leave the sheet stuck open.
    closing.current = true;
    window.history.back();
    window.setTimeout(() => {
      if (closing.current && markerInAddress()) clearMarkerInPlace();
    }, CLOSE_FALLBACK_MS);
  }

  const open = noticeId !== null && notice !== undefined;

  // Layout effect: focus and Escape are in place the moment the sheet is on screen.
  useLayoutEffect(() => {
    if (!open) return;
    previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    const focusableSelector = 'button:not([disabled]), a[href]';
    dialog?.querySelector<HTMLElement>(focusableSelector)?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      previousFocus.current?.focus();
    };
  }, [open, noticeId]);

  if (!open) return null;

  const link = notice ? safeNoticeLink(notice.url) : '/today';
  // A notice that points at plain Today has nothing more to open.
  const hasLink = !notice || link !== '/today';

  function follow(event: MouseEvent) {
    // A full load, as a tap on a notification always gave, so every sender's link
    // behaves as it did. Replacing the sheet's entry leaves no marker behind.
    event.preventDefault();
    window.location.replace(link);
  }

  return (
    <div class={css.detailBackdrop} role="presentation" onClick={close}>
      <div
        ref={dialogRef}
        class={css.detailSheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby="notice-sheet-title"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <header class={css.detailHeader}>
          <div class={css.detailHeadingWrap}>
            <div class={css.detailEyebrow}>Jarvis notification</div>
            <h2 id="notice-sheet-title" class={css.detailTitle}>{notice ? notice.title : MISSING}</h2>
            {notice && <div class={css.detailMeta}>{arrivalWords(notice.at)}</div>}
          </div>
          <button type="button" class={css.detailClose} onClick={close} aria-label="Close notification">
            ×
          </button>
        </header>

        {notice && (
          <div class={css.detailScroll}>
            <div class={css.detailBody}>{notice.detail || notice.body}</div>
          </div>
        )}

        <footer class={css.detailActions}>
          <button type="button" class={css.detailAction} onClick={close}>Close</button>
          {hasLink && (
            <a class={`${css.detailAction} ${css.detailActionPrimary}`} href={link} onClick={follow}>
              {notice ? linkLabel(link) : 'Go to Today'}
            </a>
          )}
        </footer>
      </div>
    </div>
  );
}
